import { z } from 'zod';

const optionalString = z.string().optional().catch(undefined);

const optionalBoolean = z
  .union([
    z.boolean(),
    z.literal('true').transform(() => true),
    z.literal('false').transform(() => false),
  ])
  .optional()
  .catch(undefined);

const booleanFlag = z
  .union([
    z.boolean(),
    z.literal('true').transform(() => true),
    z.literal('false').transform(() => false),
  ])
  .default(false)
  .catch(false);

/*
Ideally these would be in the same file as the route component, but we can't have files export components and other
things (eg constants) if we want React Refresh to work properly:
https://github.com/vitejs/vite-plugin-react/blob/main/packages/plugin-react/README.md#consistent-components-exports
 */

const optionalAdminConsentString = z
  .union([z.string(), z.array(z.string()).transform((value) => value[0])])
  .optional()
  .catch(undefined);

export const adminConsentSearchSchema = z.object({
  admin_consent: optionalAdminConsentString,
  error: optionalAdminConsentString,
  error_description: optionalAdminConsentString,
  tenant: optionalAdminConsentString,
});

export type AdminConsentSearch = z.infer<typeof adminConsentSearchSchema>;

export const oauthCallbackSearchSchema = z.object({
  code: optionalString,
  state: optionalString,
  error: optionalString,
  error_description: optionalString,
});

export const loginSearchSchema = z.object({
  error: optionalString,
});

export const allMattersTabSchema = z
  .enum(['matters', 'deadlines', 'conversations', 'uploads'])
  .default('matters')
  .catch('matters');

export type AllMattersTab = z.infer<typeof allMattersTabSchema>;

export const homeSearchSchema = z.object({
  archived: booleanFlag,
  viewAll: booleanFlag,
  tab: allMattersTabSchema,
});

export const ADMIN_TABS = [
  'analytics',
  'matters',
  'templates',
  'prompts',
  'syncs',
  'audit',
] as const;

export type AdminTab = (typeof ADMIN_TABS)[number];

export function isAdminTab(value: string): value is AdminTab {
  return (ADMIN_TABS as readonly string[]).includes(value);
}

export const adminSearchSchema = z.object({
  tab: z.enum(ADMIN_TABS).optional().catch(undefined),
});

export const PLATFORM_TABS = ['tenants', 'token-usage', 'sidecar-scope'] as const;

export type PlatformTab = (typeof PLATFORM_TABS)[number];

export const platformSearchSchema = z.object({
  tab: z.enum(PLATFORM_TABS).optional().catch(undefined),
});

export const overviewStages = ['early', 'overview'] as const;
export type OverviewStage = (typeof overviewStages)[number];

export const overviewSearchSchema = z.object({
  stage: z.enum(overviewStages).optional().catch(undefined),
});

export const deadlinesSearchSchema = z.object({
  taskId: optionalString,
});

export const docketsSearchSchema = z.object({
  // Docket entry guid to reveal and highlight (used by Case Partner citations).
  entry: optionalString,
});

export const casePartnerSearchSchema = z.object({
  documents: optionalString,
  /** JSON BackendDocumentSelection[] — only when parent/attachment inclusions were customized. */
  docSel: optionalString,
  draft: optionalString,
  q: optionalString,
  /** Homepage ask-box Web choice. Absent means the Case Partner composer default. */
  web: optionalBoolean,
});

export const draftDetailSearchSchema = z.object({
  version: optionalString,
  mode: z.enum(['docx', 'diff']).optional().catch(undefined),
});

export const fileViewerSearchSchema = z.object({
  from: optionalString,
  taskId: optionalString,
  chunk: optionalString,
  segment: optionalString,
  cellRange: optionalString,
  page: optionalString,
});
export type FileViewerSearch = z.infer<typeof fileViewerSearchSchema>;

export const caseFilesSearchSchema = z.object({
  view: z.enum(['deep-analysis']).optional().catch(undefined),
});

export type CaseFilesSearch = z.infer<typeof caseFilesSearchSchema>;

export const folderSearchSchema = caseFilesSearchSchema.extend({
  open: optionalString,
});
